// Clipboard capture import — the plugin side of the extension's "Copy" flow.
//
// The extension writes a text/html clipboard payload with an H2FIG marker
// envelope (mirroring the reference extension's figh2d span marker); the
// plugin's paste handler finds that marker in EITHER the text/html or
// text/plain flavor, decodes it, and imports — so ⌘+V anywhere in the plugin
// drops the captured design in. Pure + unit-tested; the UI wires the DOM
// paste event and the render dispatch around it.
import { gunzipSync } from 'fflate';

// <!--(h2fig)base64(gzip(.h2fig bytes))(/h2fig)--> — the marker envelope the
// extension writes (Export to Figma's comment-wrapped figh2d convention).
const MARKER_RE = /\(h2fig\)([A-Za-z0-9+/=]+)\(\/h2fig\)/;
// h2fig-base64:<base64(.h2fig bytes)> — the legacy plain-text form the manual
// paste box has always accepted (uncompressed).
const LEGACY_PREFIX = 'h2fig-base64:';

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Pull raw .h2fig bytes out of clipboard flavors, or null if none present.
 * Tries text/html first (the rich flavor the extension prefers), then
 * text/plain; supports the gzipped H2FIG marker and the legacy prefix.
 * gzip is detected by magic bytes, so an uncompressed marker still decodes.
 */
export function extractH2figBytes(sources: { html?: string; text?: string }): Uint8Array | null {
  for (const src of [sources.html, sources.text]) {
    if (!src) continue;
    const m = MARKER_RE.exec(src);
    if (m) {
      const raw = base64ToBytes(m[1]);
      return raw[0] === 0x1f && raw[1] === 0x8b ? gunzipSync(raw) : raw;
    }
    const idx = src.indexOf(LEGACY_PREFIX);
    if (idx !== -1) {
      const tail = src.slice(idx + LEGACY_PREFIX.length).match(/^[A-Za-z0-9+/=]+/);
      if (tail) return base64ToBytes(tail[0]);
    }
  }
  return null;
}

/** Does a paste look like a capture? Used to decide whether to intercept. */
export function clipboardHasCapture(sources: { html?: string; text?: string }): boolean {
  return extractH2figBytes(sources) != null;
}
